import React, { useState, useEffect } from 'react'
import {
  getNotificationConfig,
  updateNotificationConfig,
} from '../../api/adminNotificationsService'

/**
 * NotificationControls - Configuration panel for notification system
 */
function NotificationControls() {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState(null)
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await getNotificationConfig()

      if (response.success) {
        setConfig(response.config)
      } else {
        throw new Error(response.error || 'Failed to load config')
      }
    } catch (err) {
      console.error('Error loading config:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (updates) => {
    try {
      setSaving(true)
      setSaveMessage(null)

      const response = await updateNotificationConfig(updates)

      if (response.success) {
        setConfig(response.config)
        setSaveMessage({
          type: 'success',
          text: 'Configuration saved successfully!',
        })

        // Clear success message after 3 seconds
        setTimeout(() => setSaveMessage(null), 3000)
      } else {
        throw new Error(response.error || 'Failed to save config')
      }
    } catch (err) {
      console.error('Error saving config:', err)
      setSaveMessage({ type: 'danger', text: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleMasterToggle = (enabled) => {
    if (!enabled) {
      // Turning off - show confirmation
      setShowKillSwitchConfirm(true)
    } else {
      // Turning on - save directly
      handleSave({ master_enabled: enabled })
    }
  }

  const confirmKillSwitch = () => {
    setShowKillSwitchConfirm(false)
    handleSave({ master_enabled: false })
  }

  const handleEventTypeToggle = (eventType, enabled) => {
    const eventTypesEnabled = config.event_types_enabled || {}
    handleSave({
      event_types_enabled: {
        ...eventTypesEnabled,
        [eventType]: enabled,
      },
    })
  }

  const handleChannelToggle = (channel, enabled) => {
    const channelsEnabled = config.channels_enabled || {}
    handleSave({
      channels_enabled: {
        ...channelsEnabled,
        [channel]: enabled,
      },
    })
  }

  const handleRateLimitChange = (field, value) => {
    const rateLimits = config.rate_limits || {
      max_per_hour: 10,
      max_per_day: 50,
    }
    handleSave({
      rate_limits: {
        ...rateLimits,
        [field]: parseInt(value) || 0,
      },
    })
  }

  const handleQuietHoursChange = (field, value) => {
    const quietHours = config.quiet_hours || {
      enabled: false,
      start_hour: 22,
      end_hour: 8,
    }
    handleSave({
      quiet_hours: {
        ...quietHours,
        [field]: field === 'enabled' ? value : parseInt(value) || 0,
      },
    })
  }

  const handleCostChange = (value) => {
    const cost = parseFloat(value)
    if (!isNaN(cost) && cost >= 0) {
      handleSave({ cost_per_sms_segment: cost })
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading configuration...</span>
        </div>
        <p className="mt-2 text-muted">Loading configuration...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error Loading Configuration
        </h5>
        <p className="mb-0">{error}</p>
        <button className="btn btn-sm btn-danger mt-2" onClick={loadConfig}>
          <i className="bi bi-arrow-clockwise me-1"></i>
          Retry
        </button>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        No configuration available
      </div>
    )
  }

  const masterEnabled =
    config.master_enabled === true || config.master_enabled === 'true'
  const eventTypesEnabled = config.event_types_enabled || {}
  const channelsEnabled = config.channels_enabled || {}
  const rateLimits = config.rate_limits || { max_per_hour: 10, max_per_day: 50 }
  const quietHours = config.quiet_hours || {
    enabled: false,
    start_hour: 22,
    end_hour: 8,
  }
  const costPerSegment = config.cost_per_sms_segment || 0.75

  return (
    <div className="notification-controls">
      {/* Save Message */}
      {saveMessage && (
        <div
          className={`alert alert-${saveMessage.type} alert-dismissible fade show`}
          role="alert"
        >
          {saveMessage.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSaveMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Master Kill Switch */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-danger">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="card-title mb-1">
                    <i className="bi bi-power text-danger me-2"></i>
                    Master Kill Switch
                  </h5>
                  <p className="text-muted mb-0 small">
                    Emergency off switch for ALL notifications. Use with
                    caution.
                  </p>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="masterSwitch"
                    checked={masterEnabled}
                    onChange={(e) => handleMasterToggle(e.target.checked)}
                    disabled={saving}
                    style={{ width: '3rem', height: '1.5rem' }}
                  />
                  <label
                    className="form-check-label ms-2"
                    htmlFor="masterSwitch"
                  >
                    <strong>{masterEnabled ? 'ENABLED' : 'DISABLED'}</strong>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event Type Toggles */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-tag-fill me-2"></i>
                Event Type Toggles
              </h5>
              <p className="text-muted small mb-3">
                Enable or disable notifications for specific event types
              </p>

              <div className="row g-3">
                {Object.entries(eventTypesEnabled).map(
                  ([eventType, enabled]) => (
                    <div key={eventType} className="col-12 col-md-6">
                      <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <span className="text-capitalize">
                          {eventType.replace(/_/g, ' ')}
                        </span>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`event-${eventType}`}
                            checked={enabled}
                            onChange={(e) =>
                              handleEventTypeToggle(eventType, e.target.checked)
                            }
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Toggles */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-broadcast me-2"></i>
                Channel Toggles
              </h5>
              <p className="text-muted small mb-3">
                Enable or disable specific communication channels
              </p>

              <div className="row g-3">
                {Object.entries(channelsEnabled).map(([channel, enabled]) => (
                  <div key={channel} className="col-12 col-md-6">
                    <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                      <span className="text-uppercase">
                        <i
                          className={`bi bi-${channel === 'sms' ? 'phone' : 'envelope'} me-2`}
                        ></i>
                        {channel}
                      </span>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`channel-${channel}`}
                          checked={enabled}
                          onChange={(e) =>
                            handleChannelToggle(channel, e.target.checked)
                          }
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="row mb-4">
        <div className="col-12 col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-speedometer2 me-2"></i>
                Rate Limits
              </h5>
              <p className="text-muted small mb-3">
                Per-user notification limits to prevent spam
              </p>

              <div className="mb-3">
                <label htmlFor="maxPerHour" className="form-label">
                  Max per Hour
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="maxPerHour"
                  value={rateLimits.max_per_hour}
                  onChange={(e) =>
                    handleRateLimitChange('max_per_hour', e.target.value)
                  }
                  onBlur={(e) =>
                    handleRateLimitChange('max_per_hour', e.target.value)
                  }
                  disabled={saving}
                  min="0"
                />
              </div>

              <div className="mb-0">
                <label htmlFor="maxPerDay" className="form-label">
                  Max per Day
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="maxPerDay"
                  value={rateLimits.max_per_day}
                  onChange={(e) =>
                    handleRateLimitChange('max_per_day', e.target.value)
                  }
                  onBlur={(e) =>
                    handleRateLimitChange('max_per_day', e.target.value)
                  }
                  disabled={saving}
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="col-12 col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-moon-fill me-2"></i>
                Quiet Hours
              </h5>
              <p className="text-muted small mb-3">
                Times when notifications should not be sent (UTC)
              </p>

              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="quietHoursEnabled"
                    checked={quietHours.enabled}
                    onChange={(e) =>
                      handleQuietHoursChange('enabled', e.target.checked)
                    }
                    disabled={saving}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="quietHoursEnabled"
                  >
                    Enable Quiet Hours
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="startHour" className="form-label">
                  Start Hour (0-23)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="startHour"
                  value={quietHours.start_hour}
                  onChange={(e) =>
                    handleQuietHoursChange('start_hour', e.target.value)
                  }
                  onBlur={(e) =>
                    handleQuietHoursChange('start_hour', e.target.value)
                  }
                  disabled={saving || !quietHours.enabled}
                  min="0"
                  max="23"
                />
              </div>

              <div className="mb-0">
                <label htmlFor="endHour" className="form-label">
                  End Hour (0-23)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="endHour"
                  value={quietHours.end_hour}
                  onChange={(e) =>
                    handleQuietHoursChange('end_hour', e.target.value)
                  }
                  onBlur={(e) =>
                    handleQuietHoursChange('end_hour', e.target.value)
                  }
                  disabled={saving || !quietHours.enabled}
                  min="0"
                  max="23"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Per Segment */}
      <div className="row mb-4">
        <div className="col-12 col-md-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-currency-dollar me-2"></i>
                Cost Tracking
              </h5>
              <p className="text-muted small mb-3">
                Cost per SMS segment in cents (for cost estimation)
              </p>

              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="number"
                  className="form-control"
                  id="costPerSegment"
                  value={costPerSegment}
                  onChange={(e) => handleCostChange(e.target.value)}
                  onBlur={(e) => handleCostChange(e.target.value)}
                  disabled={saving}
                  min="0"
                  step="0.01"
                />
                <span className="input-group-text">cents</span>
              </div>
              <small className="form-text text-muted mt-1 d-block">
                Example: 0.75 = $0.0075 per segment
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Kill Switch Confirmation Modal */}
      {showKillSwitchConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Kill Switch
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowKillSwitchConfirm(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to disable <strong>ALL</strong>{' '}
                  notifications? This will immediately stop all SMS and email
                  notifications from being sent.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowKillSwitchConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmKillSwitch}
                >
                  <i className="bi bi-power me-1"></i>
                  Disable All Notifications
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationControls
